import { ButtonLoader } from "components";
import { BiCheck } from "react-icons/bi";
import { BsExclamationLg, BsClock } from "react-icons/bs";
import { Link } from "react-router-dom";
import AppDownloadLinks from "components/v2/AppDownloadLinks";
import { ECOCASH_MAX_POLL_DURATION, ECOCASH_SHOW_CANCEL_AFTER } from "utils/paymentConfig";

/**
 * PaymentStatusCard - A reusable component for displaying payment status
 * @param {string} status - 'loading' | 'success' | 'failure' | 'timeout'
 * @param {string} title - Main heading text
 * @param {string} message - Description text
 * @param {string} paymentMethod - e.g., 'EcoCash'
 * @param {object} primaryAction - { label: string, to: string } for primary CTA
 * @param {object} secondaryAction - { label: string, to: string } for secondary CTA
 * @param {string} supportUrl - URL for support link (for failure state)
 * @param {string} reference - Payment reference for support (for failure state)
 * @param {React.ReactNode} children - Optional additional content
 * @param {string} paymentType - 'Purchase' | 'Subscription'
 * @param {number} elapsedTime - Time elapsed in milliseconds (for loading state)
 * @param {function} onCancel - Callback when user clicks cancel (for loading state)
 * @param {function} onRetry - Callback when user clicks retry (for timeout state)
 * 
 */
const PaymentStatusCard = ({
	status = "loading",
	title,
	message,
	paymentMethod = "EcoCash",
	primaryAction,
	secondaryAction,
	supportUrl,
	reference,
	paymentType = "Purchase",
	children,
	elapsedTime = 0,
	onCancel,
	onRetry,
}) => {
	return (
		<div className="w-full max-w-md mx-auto px-4 sm:px-0 animate-appear">
			<div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				{/* Status Icon Section */}
				<div className="pt-8 sm:pt-10 pb-5 sm:pb-6 px-5 sm:px-8 flex flex-col items-center">
					<StatusIcon status={status} />
					
					{/* Title */}
					<h1 className="mt-5 sm:mt-6 text-xl sm:text-2xl font-bold text-gray-800 text-center">
						{title || getDefaultTitle(status)}
					</h1>
					
					{/* Message */}
					<div className="mt-3 text-center text-gray-600 text-sm sm:text-base">
						{status === "loading" ? (
							<LoadingContent 
								paymentMethod={paymentMethod} 
								elapsedTime={elapsedTime}
								onCancel={onCancel}
							/>
						) : status === "success" ? (
							<SuccessContent message={message} paymentMethod={paymentMethod} />
						) : status === "timeout" ? (
							<TimeoutContent 
								message={message} 
								paymentType={paymentType} 
								reference={reference}
								onRetry={onRetry}
							/>
						) : (
							<FailureContent message={message} paymentType={paymentType} />
						)}
					</div>
				</div>

				{/* Actions Section */}
				{status !== "loading" && status !== "timeout" && (
					<div className="px-5 sm:px-8 pb-5 sm:pb-6">
						{/* Primary Action */}
						{primaryAction && (
							<Link
								to={primaryAction.to}
								className="block w-full py-3 px-4 sm:px-6 text-center font-semibold text-white rounded-full bg-gradient-to-b from-akgreenest to-akgreen hover:opacity-90 active:opacity-80 transition-opacity shadow-md text-sm sm:text-base"
							>
								{primaryAction.label}
							</Link>
						)}
						
						{/* Secondary Action */}
						{secondaryAction && (
							<Link
								to={secondaryAction.to}
								className="block w-full mt-3 py-3 px-4 sm:px-6 text-center font-medium text-akgreener rounded-full border-2 border-akgreener hover:bg-akgreener hover:text-white active:bg-akgreenest transition-colors text-sm sm:text-base"
							>
								{secondaryAction.label}
							</Link>
						)}

						{/* Support Link for Failure */}
						{status === "failure" && supportUrl && (
							<div className="mt-4 text-center">
								<Link
									to={supportUrl}
									className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 active:text-blue-900 font-medium underline text-sm sm:text-base"
								>
									Contact Support
								</Link>
								{reference && (
									<p className="mt-1 text-xs text-gray-500 break-all">
										Reference: {reference}
									</p>
								)}
							</div>
						)}
					</div>
				)}

				{/* Additional Content */}
				{children && (
					<div className="px-5 sm:px-8 pb-5 sm:pb-6">
						{children}
					</div>
				)}

				{/* App Download Section (for success) */}
				{status === "success" && (
					<div className="px-5 sm:px-8 pb-6 sm:pb-8">
						<div className="pt-4 border-t border-gray-200">
							<AppDownloadLinks compact />
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

// Status Icon Component
const StatusIcon = ({ status }) => {
	if (status === "loading") {
		return (
			<div className="relative">
				<div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center">
					<ButtonLoader size={50} />
				</div>
				<div className="absolute inset-0 rounded-full border-4 border-akgreen border-t-transparent animate-spin" style={{ animationDuration: '1.5s' }} />
			</div>
		);
	}

	if (status === "success") {
		return (
			<div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-akgreen to-akgreener flex items-center justify-center shadow-lg animate-success-bounce">
				<BiCheck className="text-white w-14 h-14 sm:w-16 sm:h-16" />
			</div>
		);
	}

	if (status === "timeout") {
		return (
			<div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-akgreener to-akgreen flex items-center justify-center shadow-lg animate-fade-in-up">
				<BsClock className="text-white w-10 h-10 sm:w-12 sm:h-12" />
			</div>
		);
	}

	// Failure
	return (
		<div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-akgreenest to-akgreen flex items-center justify-center shadow-lg animate-fade-in-up">
			<BsExclamationLg className="text-white w-10 h-10 sm:w-12 sm:h-12" />
		</div>
	);
};

// Format elapsed time for display
const formatElapsedTime = (ms) => {
	const totalSeconds = Math.floor(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	
	if (minutes > 0) {
		return `${minutes}m ${seconds}s`;
	}
	return `${seconds}s`;
};

// Loading Content
const LoadingContent = ({ paymentMethod, elapsedTime = 0, onCancel }) => {
	const showCancelButton = elapsedTime >= ECOCASH_SHOW_CANCEL_AFTER && onCancel;
	const progressPercent = Math.min((elapsedTime / ECOCASH_MAX_POLL_DURATION) * 100, 100);

	return (
		<div className="space-y-4">
			<p className="text-gray-600">
				Please confirm the <span className="font-semibold">{paymentMethod}</span> PIN prompt on your phone.
			</p>
			
			{/* Progress Steps */}
			<div className="mt-6 space-y-3">
				<ProgressStep step={1} label="Payment initiated" completed />
				<ProgressStep step={2} label="Waiting for confirmation" active />
				<ProgressStep step={3} label="Processing complete" />
			</div>

			{/* Elapsed Time Display */}
			{elapsedTime > 0 && (
				<div className="text-center pt-2">
					<span className="text-sm text-gray-500">
						Waiting for <span className="font-semibold text-akgreener">{formatElapsedTime(elapsedTime)}</span>
					</span>
					{/* Progress Bar */}
					<div className="mt-2 w-full max-w-xs mx-auto">
						<div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
							<div 
								className="h-full bg-gradient-to-r from-akgreen to-akgreener transition-all duration-500 ease-out"
								style={{ width: `${progressPercent}%` }}
							/>
						</div>
					</div>
				</div>
			)}
			
			<p className="text-sm text-akgreener font-medium animate-pulse mt-4">
				We'll update this page automatically...
			</p>

			{/* Cancel Button */}
			{showCancelButton && (
				<button
					type="button"
					onClick={onCancel}
					className="text-sm text-gray-500 hover:text-gray-700 underline transition-colors mt-2"
				>
					Cancel and try again
				</button>
			)}
		</div>
	);
};

// Progress Step Component
const ProgressStep = ({ step, label, completed, active }) => (
	<div className="flex items-center gap-3">
		<div
			className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
				completed
					? "bg-akgreener text-white"
					: active
					? "bg-akgreen text-white animate-pulse"
					: "bg-gray-200 text-gray-500"
			}`}
		>
			{completed ? "✓" : step}
		</div>
		<span className={`text-sm ${completed ? "text-akgreener font-medium" : active ? "text-gray-800 font-medium" : "text-gray-400"}`}>
			{label}
		</span>
	</div>
);

// Success Content
const SuccessContent = ({ message, paymentMethod }) => (
	<div>
		<p className="text-gray-600">
			{message || (
				<>
					Your <span className="font-semibold">{paymentMethod}</span> payment was recorded successfully.
				</>
			)}
		</p>
	</div>
);

// Failure Content
const FailureContent = ({ message, paymentType }) => (
	<div className="space-y-4">
		<p className="text-gray-600">
			{message || "We couldn't confirm your payment."}
		</p>
		
		{/* Common reasons */}
		<div className="text-left bg-gray-50 rounded-lg p-4 mt-4">
			<p className="text-sm font-medium text-gray-700 mb-2">This might happen if:</p>
			<ul className="text-sm text-gray-600 space-y-1">
				<li className="flex items-start gap-2">
					<span className="text-orangelib">•</span>
					PIN was not entered in time
				</li>
				<li className="flex items-start gap-2">
					<span className="text-orangelib">•</span>
					Wrong PIN was entered
				</li>
				<li className="flex items-start gap-2">
					<span className="text-orangelib">•</span>
					Insufficient wallet balance
				</li>
				<li className="flex items-start gap-2">
					<span className="text-orangelib">•</span>
					Network connection issue
				</li>
				
			</ul>
		</div>
		
	<p className="text-sm text-gray-500"> 
	{paymentType === "Purchase" ? (
		<>
			If money was deducted from your wallet, please check <Link to="/my-library" className="text-akgreener underline font-bold">My Library</Link> after 30 minutes. 
			If your books still haven't appeared, contact support for assistance.
		</>
	) : (
		<>
			If money was deducted from your wallet, please check <Link to="/my-account/subscriptions" className="text-akgreener underline font-bold">My Subscription</Link> after 30 minutes. 
			If your subscription still hasn't appeared, contact support for assistance.
		</>
	)}
	
	</p>
	</div>
);

// Timeout Content
const TimeoutContent = ({ message, paymentType, reference, onRetry }) => (
	<div className="space-y-4">
		<p className="text-gray-600">
			{message || "We're still waiting for payment confirmation from EcoCash. This is taking longer than expected."}
		</p>
		
		{/* What you can do */}
		<div className="text-left bg-amber-50 rounded-lg p-4 mt-4 border border-amber-200">
			<p className="text-sm font-medium text-amber-800 mb-2">What you can do:</p>
			<ul className="text-sm text-amber-700 space-y-2">
				<li className="flex items-start gap-2">
					<span className="text-amber-500">•</span>
					Check your phone for any pending EcoCash prompts
				</li>
				<li className="flex items-start gap-2">
					<span className="text-amber-500">•</span>
					{paymentType === "Purchase" ? (
						<p>
							If money was deducted, check <Link to="/my-library" className="text-akgreener underline font-semibold">My Library</Link> in 30 minutes
						</p>
					) : (
						<p>
							If money was deducted, check <Link to="/my-account/subscriptions" className="text-akgreener underline font-semibold">My Subscription</Link> in 30 minutes
						</p>
					)}
				</li>
				<li className="flex items-start gap-2">
					<span className="text-amber-500">•</span>
					<Link to="/support" className="text-akgreener underline font-semibold">Contact support</Link> if the issue persists
				</li>
			</ul>
		</div>

		{/* Reference */}
		{reference && (
			<p className="text-xs text-gray-500 text-center">
				Reference: <span className="font-mono">{reference}</span>
			</p>
		)}

		{/* Action Buttons */}
		<div className="flex flex-col gap-3 pt-2">
			{onRetry && (
				<button
					type="button"
					onClick={onRetry}
					className="w-full py-3 px-4 text-center font-semibold text-white rounded-full bg-gradient-to-b from-akgreenest to-akgreen hover:opacity-90 active:opacity-80 transition-opacity shadow-md text-sm"
				>
					Try Again
				</button>
			)}
			<Link
				to={paymentType === "Purchase" ? "/checkout" : "/subscriptions"}
				className="w-full py-3 px-4 text-center font-medium text-akgreener rounded-full border-2 border-akgreener hover:bg-akgreener hover:text-white active:bg-akgreenest transition-colors text-sm"
			>
				Return to {paymentType === "Purchase" ? "Checkout" : "Subscriptions"}
			</Link>
		</div>
	</div>
);

// Helper function for default titles
const getDefaultTitle = (status) => {
	switch (status) {
		case "loading":
			return "Confirming Payment";
		case "success":
			return "Payment Successful!";
		case "failure":
			return "Payment Not Completed";
		case "timeout":
			return "Confirmation Timeout";
		default:
			return "Payment Status";
	}
};

export default PaymentStatusCard;
